/**
 * ImageUploadField
 *
 * Reusable upload + crop control. Used for the partner retailer logo and
 * splash image. Splits into two surfaces:
 *
 *   1. Inline row — text input for direct URL entry, an "Upload" button,
 *      a "Clear" button, and a small live preview of the current value.
 *      Lets advanced users paste a known-good URL and skips the modal.
 *
 *   2. Cropper modal — opened on file pick. Loads the picked image into a
 *      ReactCrop wrapper, exposes aspect-ratio presets (free + caller-
 *      provided), and on Apply renders the cropped region to a canvas,
 *      uploads the resulting Blob to /api/admin-upload.php, and surfaces
 *      the returned URL through onChange.
 *
 * Server-side cleanup: the upload endpoint deletes any prior file that
 * matches `<kind>-<retailerId>-*` so a re-crop doesn't accumulate stale
 * uploads on disk.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PercentCrop,
  type PixelCrop,
} from 'react-image-crop';
import { Upload, X, Crop as CropIcon, Loader2, AlertTriangle } from 'lucide-react';
import 'react-image-crop/dist/ReactCrop.css';

const API_BASE = '/api';

export interface AspectPreset {
  /** Visible label on the toggle button (e.g. "Wide 4:1"). */
  label: string;
  /** undefined = free crop (no aspect lock). */
  aspect: number | undefined;
}

interface ImageUploadFieldProps {
  /** Current image URL (relative or absolute), or null. */
  value: string | null;
  /** Fires after a successful upload OR a manual URL edit OR clear. */
  onChange: (next: string | null) => void;
  /** Passed to the upload endpoint to namespace stored files + cleanup. */
  retailerId: number;
  /** Drives filename prefix + per-kind dimension caps server-side. */
  kind: 'logo' | 'splash' | 'watermark';
  /** Allow retailerId=0 as the universal site-content upload namespace. */
  allowUniversalUpload?: boolean;
  /** Aspect presets surfaced as toggles in the cropper modal. */
  aspectPresets: AspectPreset[];
  /** Default aspect on modal open. Index into aspectPresets. */
  defaultPresetIndex?: number;
  /** Small placeholder under the URL input. */
  placeholder?: string;
  /** Small caption under the URL input (helper / dimension hint). */
  helpText?: string;
  /** Optional preview frame background (e.g. partner nav bg) for logo preview. */
  previewBg?: string;
  /** Disables all controls (e.g. when modal is read-only). */
  disabled?: boolean;
  /** Maximum input file size in bytes; default 8 MB. */
  maxBytes?: number;
}

const DEFAULT_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Center an initial crop inside a freshly-loaded image. When `aspect` is
 * undefined we still seed a centered 80% crop so the user has something
 * to drag from.
 */
function buildInitialCrop(imgWidth: number, imgHeight: number, aspect: number | undefined): PercentCrop {
  if (aspect && aspect > 0) {
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 80 }, aspect, imgWidth, imgHeight),
      imgWidth,
      imgHeight,
    );
  }
  return { unit: '%', x: 10, y: 10, width: 80, height: 80 };
}

/**
 * Render the chosen crop region from a source <img> into a canvas blob.
 * The PercentCrop is converted to absolute pixels using the image's
 * natural dimensions so the resulting blob is at full source resolution
 * (not display-scaled), which is what we want for nav logos that may
 * render across a range of pixel densities.
 */
async function cropToBlob(
  imgEl: HTMLImageElement,
  crop: PixelCrop,
  mimeType: string,
): Promise<Blob> {
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;
  const px = {
    x: Math.round(crop.x * scaleX),
    y: Math.round(crop.y * scaleY),
    w: Math.round(crop.width * scaleX),
    h: Math.round(crop.height * scaleY),
  };

  const canvas = document.createElement('canvas');
  canvas.width = px.w;
  canvas.height = px.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not allocate canvas context');
  ctx.drawImage(imgEl, px.x, px.y, px.w, px.h, 0, 0, px.w, px.h);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null'))),
      mimeType,
      0.92,
    );
  });
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  value,
  onChange,
  retailerId,
  kind,
  aspectPresets,
  defaultPresetIndex = 0,
  placeholder,
  helpText,
  previewBg,
  disabled,
  maxBytes = DEFAULT_MAX_BYTES,
  allowUniversalUpload = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Modal lifecycle ---------------------------------------------------------
  const [pickedObjectUrl, setPickedObjectUrl] = useState<string | null>(null);
  const [pickedMime, setPickedMime] = useState<string>('image/png');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Cropper state -----------------------------------------------------------
  const [presetIdx, setPresetIdx] = useState(defaultPresetIndex);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  // Object URL leaks if not revoked; tear it down whenever the modal closes
  // or a different file is picked.
  useEffect(() => {
    return () => {
      if (pickedObjectUrl) URL.revokeObjectURL(pickedObjectUrl);
    };
  }, [pickedObjectUrl]);

  const closeModal = useCallback(() => {
    if (pickedObjectUrl) URL.revokeObjectURL(pickedObjectUrl);
    setPickedObjectUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setError(null);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [pickedObjectUrl]);

  /**
   * Direct upload (no crop) — used for SVG since vector files don't
   * benefit from cropping and the cropper relies on a raster canvas
   * pipeline. Sends the file as-is; the server sanitizes SVG content.
   */
  const uploadRawFile = useCallback(async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('kind', kind);
      fd.append('retailerId', String(retailerId));
      const res = await fetch(`${API_BASE}/admin-upload.php`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `Upload failed (${res.status})`);
      if (typeof body.url !== 'string') throw new Error('Upload response missing url');
      onChange(body.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [kind, retailerId, onChange]);

  const onFilePicked = (file: File) => {
    setError(null);
    if (file.size > maxBytes) {
      setError(`File too large (max ${Math.round(maxBytes / (1024 * 1024))} MB).`);
      return;
    }
    // SVG bypass — vector, no crop needed. Some browsers report SVG MIME
    // as empty or text/xml depending on file source, so fall back to the
    // .svg extension when the type field is unhelpful.
    const isSvg = file.type === 'image/svg+xml'
      || file.type === 'image/svg'
      || (file.type === '' && /\.svg$/i.test(file.name));
    if (isSvg) {
      uploadRawFile(file);
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Unsupported type. Use PNG, JPEG, WebP, or SVG.');
      return;
    }
    if (pickedObjectUrl) URL.revokeObjectURL(pickedObjectUrl);
    setPickedObjectUrl(URL.createObjectURL(file));
    setPickedMime(file.type);
    setPresetIdx(defaultPresetIndex);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const aspect = aspectPresets[presetIdx]?.aspect;
    setCrop(buildInitialCrop(width, height, aspect));
  };

  // Switching aspect re-seeds the crop so the new constraint takes effect
  // immediately rather than waiting for the user to drag.
  const switchPreset = (idx: number) => {
    setPresetIdx(idx);
    if (imgRef.current) {
      const aspect = aspectPresets[idx]?.aspect;
      setCrop(buildInitialCrop(imgRef.current.width, imgRef.current.height, aspect));
    }
  };

  const handleApply = async () => {
    if (!imgRef.current || !completedCrop || completedCrop.width < 1 || completedCrop.height < 1) {
      setError('Drag a crop region first.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      // Always re-encode as PNG when the source was PNG (preserves
      // transparency) — otherwise JPEG, since logos and splash images
      // never need an alpha channel from a JPEG source.
      const outMime = pickedMime === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await cropToBlob(imgRef.current, completedCrop, outMime);

      const fd = new FormData();
      fd.append('file', blob, `crop.${outMime === 'image/png' ? 'png' : 'jpg'}`);
      fd.append('kind', kind);
      fd.append('retailerId', String(retailerId));

      const res = await fetch(`${API_BASE}/admin-upload.php`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? `Upload failed (${res.status})`);
      if (typeof body.url !== 'string') throw new Error('Upload response missing url');

      onChange(body.url);
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // The "save first" guard: an unsaved retailer (id <= 0) has nowhere to
  // store an upload yet. Surface that inline so the admin gets a concrete
  // explanation instead of a 400 from the endpoint.
  const canUpload = retailerId > 0 || (allowUniversalUpload && retailerId === 0);

  return (
    <>
      <div className="mb-2">
        <div className="input-group input-group-sm">
          <input
            type="text"
            className="form-control"
            value={value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            onChange={e => onChange(e.target.value || null)}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !canUpload || uploading}
            title={canUpload ? 'Upload (PNG/JPEG/WebP get a cropper; SVG uploads as-is)' : 'Save the retailer first to enable uploads'}
          >
            {uploading && !pickedObjectUrl ? (
              <><Loader2 size={12} className="me-1" style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
            ) : (
              <><Upload size={12} className="me-1" /> Upload</>
            )}
          </button>
          {value && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => onChange(null)}
              disabled={disabled}
              title="Clear"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {!canUpload && (
          <div className="form-text small text-warning d-flex align-items-center gap-1">
            <AlertTriangle size={11} /> Save the retailer first to enable uploads.
          </div>
        )}
        {helpText && <div className="form-text small">{helpText}</div>}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFilePicked(f);
          }}
        />

        {/* Direct-upload errors (SVG path) surface here since there's no
            cropper modal to show them in. */}
        {error && !pickedObjectUrl && (
          <div className="form-text small text-danger d-flex align-items-center gap-1 mt-1">
            <AlertTriangle size={11} /> {error}
          </div>
        )}

        {value && (
          <div className="mt-2 d-inline-block">
            <div
              className="border rounded-3 p-2"
              style={{
                background: previewBg ?? '#f8f9fa',
                maxWidth: kind === 'splash' ? 360 : 320,
              }}
            >
              <img
                src={value}
                alt=""
                style={{
                  maxHeight: kind === 'splash' ? 160 : 56,
                  maxWidth: kind === 'splash' ? 340 : 280,
                  display: 'block',
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Cropper modal */}
      {pickedObjectUrl && (
        <div
          className="modal d-block fade show"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1090 }}
          onClick={(e) => { if (e.target === e.currentTarget && !uploading) closeModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <CropIcon size={16} />
                  Crop {kind === 'logo' ? 'logo' : 'splash image'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeModal}
                  disabled={uploading}
                />
              </div>

              <div className="modal-body">
                {error && (
                  <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                  <span className="small fw-semibold">Aspect:</span>
                  {aspectPresets.map((p, i) => (
                    <button
                      key={p.label}
                      type="button"
                      className={`btn btn-sm ${i === presetIdx ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => switchPreset(i)}
                      disabled={uploading}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <div className="bg-light rounded-3 p-2 d-flex justify-content-center" style={{ maxHeight: '60vh', overflow: 'auto' }}>
                  <ReactCrop
                    crop={crop}
                    onChange={(_pixelCrop, percentCrop) => setCrop(percentCrop)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={aspectPresets[presetIdx]?.aspect}
                    keepSelection
                    minWidth={16}
                    minHeight={16}
                    disabled={uploading}
                  >
                    <img
                      ref={imgRef}
                      src={pickedObjectUrl}
                      alt="Crop source"
                      onLoad={onImageLoad}
                      style={{ maxHeight: '55vh', maxWidth: '100%' }}
                    />
                  </ReactCrop>
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeModal}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                  onClick={handleApply}
                  disabled={uploading || !completedCrop}
                >
                  {uploading ? (
                    <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
                  ) : (
                    <><Upload size={14} /> Apply &amp; upload</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
